/**
 * Staff applications queue — SUBMITTED, oldest first, exactly as the API
 * returns it. Invariant 8 requires applications be decided in submittedAt
 * order within a unit; the UI does not offer a re-sort, on purpose — the
 * server's order is the only order.
 */

import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';

interface ApplicationQueueRow {
  id: string;
  status: string;
  submittedAt: string | null;
  applicantName: string;
  applicantEmail: string;
  unitLabel: string;
  propertyName: string;
}

const columns: Column<ApplicationQueueRow>[] = [
  {
    key: 'applicant',
    header: 'Applicant',
    render: (row) => (
      <>
        <strong>{row.applicantName}</strong>
        <div className="muted">{row.applicantEmail}</div>
      </>
    ),
  },
  {
    key: 'unit',
    header: 'Unit',
    render: (row) => (
      <>
        {row.unitLabel}, {row.propertyName}
      </>
    ),
  },
  {
    key: 'submittedAt',
    header: 'Submitted',
    render: (row) => (row.submittedAt ? row.submittedAt.slice(0, 16).replace('T', ' ') : '—'),
  },
];

export function ApplicationQueue() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useList<ApplicationQueueRow>({
    resource: 'staff/applications',
    pagination: { mode: 'off' },
  });

  const rows = data?.data ?? [];

  return (
    <section className="stack">
      <div className="row">
        <h1>Applications</h1>
        <span className="shell__spacer" />
      </div>
      <p className="muted">
        {rows.length} awaiting a decision, oldest first — process in this order (invariant 8).
      </p>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        onRowClick={(row) => void navigate(`/staff/applications/${row.id}`)}
        emptyMessage="Nothing waiting on a decision."
      />
    </section>
  );
}
